import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { OrderedList } from '@components/common/OrderedList'
import { PageLayout } from '@components/common/PageLayout'
import { DashboardBackButton } from '@components/utils'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import { SECTION_NAMES } from '@constants/sectionNames'
import { Checkbox, HeadContent, Skeleton, Subsection } from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { ConnectBar } from '../../components/ConnectBar/ConnectBar'
import { YouTubeVideoEntry } from '../../components/YouTubeVideoEntry/YouTubeVideoEntry'
import type { FormValues } from './AppsTiktok.types'
import { useTiktok } from './hooks/useTiktok'

export const AppsTiktok = () => {
  const { tiktok } = useTiktok()
  const formik = useFormik<FormValues>({
    initialValues: tiktok.settings.initialValues,
    enableReinitialize: true,
    onSubmit: tiktok.settings.onSubmit,
  })

  const renderSettings = () => {
    if (tiktok.status !== 'CONNECTED') {
      return null
    }

    return (
      <form onSubmit={formik.handleSubmit}>
        <Subsection title="Share thank you notes">
          <div>
            Optionally we can send all of your video thank you notes directly to your TikTok inbox
            where you’ll be able to easily share them with everyone.
          </div>
          <div style={{ marginTop: 16 }}>
            <Checkbox
              label="Automatically send my video thank you notes to my TikTok inbox"
              {...formik.getFieldProps('kudosSync')}
            />
          </div>
        </Subsection>
        <ConfirmBar
          isOpen={formik.dirty}
          isLoading={tiktok.settings.isSubmitting}
          onConfirm={formik.submitForm}
          onCancel={formik.resetForm}
        />
        <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
      </form>
    )
  }

  return (
    <>
      <HeadContent title={SECTION_NAMES.appTikTok} />
      <PageLayout size="tiny">
        <Dashboard.Section
          name={SECTION_NAMES.appTikTok}
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
          bottomContent={
            <div style={{ lineHeight: 1.5 }}>
              <p>
                Highlight your recent TikTok videos on your homepage and easily share “thank you”
                notes to your TikTok account.
              </p>
              <OrderedList>
                <OrderedList.Item>Connect your TikTok account.</OrderedList.Item>
                <OrderedList.Item>
                  Add “TikTok feed” section to your homepage on {SECTION_NAMES.themeEditor} tab.
                </OrderedList.Item>
              </OrderedList>
            </div>
          }
        >
          <div style={{ marginTop: 16 - 24, marginBottom: 40 }}>
            {tiktok.status && (
              <div style={{ marginBlock: 20 }}>
                <YouTubeVideoEntry
                  title="How to Display Your TikTok Feed on Your Shop"
                  description="Learn how to feature your TikTok videos on your shop page and keep your audience connected to your content."
                  size={tiktok.status === 'NOT_CONNECTED' ? 'regular' : 'compact'}
                  videoId="ZPtXoO5XL_4"
                />
              </div>
            )}
            {tiktok.connectBar ? (
              <ConnectBar {...tiktok.connectBar} />
            ) : (
              <Skeleton>
                <Skeleton.Shape height={64} />
              </Skeleton>
            )}
          </div>

          {renderSettings()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
